// THE BOMBARDMENT — fake chips raining onto the felt during the betting window.
//
// ── THE FIREWALL ─────────────────────────────────────────────────────────
// This is the single most dangerous file in the game: it draws objects that
// look exactly like real money, on the same felt, at the same moment the player
// is placing real bets. Six structural rules keep it inert. They are structural
// on purpose — a comment saying "don't do X" is not a firewall.
//
//   F1  The type is BRANDED and structurally incompatible with a real bet. Its
//       value field is `denom`, never `amount`; it has no `id`, no `kind`, no
//       `numbers`. TypeScript therefore rejects a FakeChip everywhere a Bet is
//       expected, so the leak cannot be written by accident.
//   F2  Fakes NEVER call makeBet(). A fake calling it would mint `straight:17`
//       — colliding with a real bet id — and amountOf('straight:17') would then
//       inflate the real rail badge. Only GEOMETRY is imported, never identity.
//   F3  ZERO import of ./engine. Enforced by a script, because there is no
//       ESLint config in player-frontend/.
//   F4  Rendered by a SIBLING layer, never through Overlay's `chips` prop —
//       down that path they would be PlacedChip[] and one .concat() from
//       merging with the real ones.
//   F5  No React state, ever. The pool lives in a ref and is written straight
//       to the DOM, so nothing fake can reach a useMemo that feeds totalBet.
//   F6  Inert to hit-testing: pointer-events none, and NO data-n / data-bet-kind
//       attributes. cellUnder() does closest('[data-n]') and railAt() does
//       closest('[data-bet-kind]') — an attribute-free node still changes which
//       element is topmost at a cell boundary, so it must also not be hittable.
//
// The eight ways this could leak into real money, ranked by how quietly they
// fail, are listed in docs/ROULETTE_UI_V2_BUILD_PLAN.md S9. The two that would
// ship silently: setLastBets -> rebet() re-staking a fake with REAL balance one
// round later, and pushing fakes into Overlay's chips array because it looks
// like the obvious shortcut.

import { cellRect, linePoints, pitchX, pitchY, type Lattice } from './lattice';

/** F1 — branded, and deliberately shaped so it cannot stand in for a Bet. */
export type FakeChip = {
  readonly __fake: true;
  node: HTMLElement;
  denom: number;          // NOT `amount`
  dieAt: number;
};

/** A place a fake chip may land, in lattice-local px. */
export type FakeTarget = { x: number; y: number };

/** The denominations the tray offers, so a fake is visually identical to a real
 *  chip. Values only — no bet, no id, no payout. */
const DENOMS = [10, 50, 100, 500, 1000] as const;

/**
 * Every legal landing spot, derived from the SAME geometry the player's own
 * studs are drawn from — so fakes land on real splits, corners and six-lines
 * rather than at arbitrary pixels, which is what makes the table look busy
 * with plausible betting rather than sprayed with confetti.
 *
 * Rail anchors are computed ARITHMETICALLY from the rail widths and the row
 * spans. Deliberately not from offsetLeft or elementFromPoint: those are the
 * real input path, and the fake layer must never touch it.
 */
export function buildTargets(L: Lattice, railL: number, railR: number): FakeTarget[] {
  const out: FakeTarget[] = [];
  const PY = pitchY(L);
  const PX = pitchX(L);

  // 79 line points — splits, streets, corners, six lines
  for (const p of linePoints(L)) out.push({ x: p.x, y: p.y });

  // 37 straight-up centres
  for (let r = 0; r <= 12; r++) {
    for (let c = 0; c < (r === 0 ? 1 : 3); c++) {
      const q = cellRect(L, r === 0 ? { r: 0, c: 0, span: 3 } : { r, c });
      out.push({ x: q.x + q.width / 2, y: q.y + q.height / 2 });
    }
  }

  /* ── THE TWELVE OUTSIDE BETS ────────────────────────────────────────────
   * Six even-money on the left rail, three dozens on the right, three columns
   * along the bottom. These are the ones the owner reported as mis-landing:
   * "they are not landing on proper numbers or the proper extra parts where a
   * user could be on multiple numbers at once."
   *
   * The old arithmetic had two faults, and one of them was severe.
   *
   *   THE DOZENS WERE ON THE WRONG SIDE OF THE BOARD. Their x was
   *   `gridW - railL + 22` — a guess with a magic 22 in it. The board is five
   *   equal columns (Board.tsx lays it out `repeat(5, minmax(0, 1fr))`), so with
   *   railL = railR = cellW that evaluates to 2·cellW + 22 where the right rail's
   *   centre is at 3.5·cellW: 64 px out at a 430 px canvas, which lands those
   *   chips in the middle of the THIRD NUMBER COLUMN. Every dozen chip was
   *   landing on the numbers.
   *
   *   AND EVERY RAIL ANCHOR WAS HALF A CELL HIGH. `(2i + 1.5) · PY` is the centre
   *   of the FIRST row of the pair a bet covers, not the centre of the pair. The
   *   `+ cellH/2` that turns a row INDEX into a row CENTRE was missing.
   *
   * Everything below is derived, with no constant in it that is not a row count.
   * Lattice-local x = 0 is the left edge of number column 0, so the left rail is
   * at negative x and the right rail begins where the numbers end.
   *
   * Still arithmetic, never the DOM: offsetLeft and elementFromPoint are the real
   * input path and the fake layer may not touch them (F2/F6).
   */
  const numbersW = 3 * L.cellW + 2 * L.gap;
  const leftX = -(L.gap + railL / 2);
  const rightX = numbersW + L.gap + railR / 2;

  // six even-money bets, two number rows each: 1-2, 3-4, 5-6, 7-8, 9-10, 11-12
  for (let i = 0; i < 6; i++) out.push({ x: leftX, y: (2 * i + 1.5) * PY + L.cellH / 2 });
  // three dozens, four rows each: 1-4, 5-8, 9-12
  for (let d = 0; d < 3; d++) out.push({ x: rightX, y: (4 * d + 2.5) * PY + L.cellH / 2 });
  // three columns, on the 2:1 strip that is row 13
  for (let c = 0; c < 3; c++) out.push({ x: c * PX + L.cellW / 2, y: 13 * PY + L.cellH / 2 });

  return out;
}

/** Weighted pick: mostly straight-ups and lines, a few on the rails — roughly
 *  how a real table fills up. */
export function pickTarget(targets: FakeTarget[], rnd: () => number): FakeTarget {
  return targets[Math.floor(rnd() * targets.length)];
}

export function pickDenom(rnd: () => number): number {
  const r = rnd();
  return r < 0.42 ? DENOMS[0] : r < 0.70 ? DENOMS[1] : r < 0.88 ? DENOMS[2]
    : r < 0.97 ? DENOMS[3] : DENOMS[4];
}

/* ─── timings ─────────────────────────────────────────────────────────────
 * 380ms flight + 500ms hold + 180ms fade = 1060ms of life. One self-scheduling
 * timer emitting 1-2 chips every 60-110ms gives ~13.6 alive at once, which is
 * the "~14" the reference shows, from a pool of 24 nodes.
 */
export const FLIGHT_MS = 380;
export const HOLD_MS = 500;
export const FADE_MS = 180;
export const LIFE_MS = FLIGHT_MS + HOLD_MS + FADE_MS;

/* ── HOW BUSY THE TABLE LOOKS ─────────────────────────────────────────────
 * The old numbers — a pool of 24, one or two chips every 60-110 ms — gave about
 * 14 alive at once, which was the count in the reference frame. But the tile
 * beside them says four thousand people are playing, and fourteen chips does not
 * look like four thousand people; it looks like a handful. The owner's words:
 * "the number of people it shows in total compared to that, the fittings are not
 * coming that good — the fake amount is not flowing that good."
 *
 * Two to four chips every 42-78 ms is roughly 50 a second, and against a 1.06 s
 * life that is about 50 in the air at any moment — a table that is genuinely
 * busy rather than one with a few players at it. The pool is sized above that so
 * `fire()` never runs dry mid-window, because a pool that exhausts does not
 * degrade smoothly: it stutters.
 *
 * The cost is bounded and it is all on the compositor. Each chip is two style
 * writes and a transition on transform and opacity — no rAF, no layout read, no
 * elementFromPoint. 72 nodes at 50 transitions a second is well inside budget on
 * the phones this ships to, and it never touches the wheel's own frame. */
export const POOL = 72;
export const EMIT_MIN_MS = 42;
export const EMIT_MAX_MS = 78;

/**
 * WHERE THE CHIPS COME FROM.
 *
 * They used to launch from off-board upper-right, which is what the reference
 * frame happened to catch mid-flight. But a chip that arrives from empty space
 * reads as decoration; a chip that leaves a face and lands on 17 reads as that
 * person betting on 17, which is the entire point of the layer.
 *
 * FOUR sources now, not three — the three ranked seats and the ALL PLAYERS
 * tile below them — and the split is deliberately lopsided. The crowd tile
 * stands for four thousand people and the named seats for one each, so if the
 * chips came evenly off all four the animation would contradict the number
 * printed on the tile. Roughly four chips in five leave the crowd; the rest are
 * shared between the top three, which is enough to keep those tiles alive
 * without making three people look like they are betting the whole table.
 *
 * ── the coordinates ──────────────────────────────────────────────────────
 * These are LATTICE-LOCAL: x = 0 is the left edge of number column 0, so the
 * left rail occupies -railL..0 and the gutter the tiles sit in is further left
 * still. GUTTER_X is the tile centre measured out from the rail, and the four
 * y values are the tile centres as a fraction of the grid's own height — the
 * same fractions social.css positions them at, kept in step by RLT_LB_ROWS
 * which both files quote.
 *
 * They are approximate ON PURPOSE. A chip that departs from the exact pixel
 * centre of a face every time draws the eye to the launcher; a chip that leaves
 * from somewhere around it reads as a hand reaching in.
 */

/** Tile centres down the gutter, as a fraction of the grid's height.
 *  Ranks 1-3, then the crowd. MEASURED off the live layout at 430x941 — the
 *  first pass at these was derived from the CSS by hand and put the crowd's
 *  launch point 42 px above its own tile, which on screen is a chip leaving from
 *  the gap between two faces. Mirrors .rlt-lb's top and --rlt-lb-pitch. */
export const LB_ROWS = [0.206, 0.430, 0.653, 0.885] as const;

/** How far LEFT of the rail's outer edge a tile's centre sits, in px per unit
 *  of rail width. The gutter and the rail both scale with the canvas, so one
 *  ratio holds at every size. */
export const LB_GUTTER_K = 0.62;   /* verified live: launch x -93.3 against a
                                      measured tile centre of -92.3 */

export function launchPoint(
  railL: number, gridH: number, which: 0 | 1 | 2 | 3, rnd: () => number,
) {
  /* The crowd tile (which=3) is a PILE of people, so its chips leave from all
     across the tile — a spread of ~1.4 rail-widths — while a named seat's chips
     leave from that one portrait (tight ±6px). One origin for the crowd made
     every chip appear from a single point and the tile never read as the
     collective source. (Owner, 2026-09-01.) */
  const spreadX = which === 3 ? railL * 1.4 : 12;
  const spreadY = which === 3 ? 34 : 16;
  return {
    x: -(railL * (1 + LB_GUTTER_K)) + (rnd() - 0.5) * spreadX,
    y: gridH * LB_ROWS[which] + (rnd() - 0.5) * spreadY,
  };
}
